import {
  HashIcon,
  UserRoundIcon,
  CircleDollarSignIcon,
  TypeIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  BookUserIcon,
  CalendarIcon,
  GlobeIcon,
} from 'lucide-react';

import { VariableDefinition } from './types/column-mapping';

/**
 * Ephemeral sentinel campaign id used by outbound conversations (live-chat / API sends).
 * No `campaigns` row exists for it. Mirrors orchestrator EPHEMERAL_CAMPAIGN_ID in
 * src/modules/campaigns/constants.ts (the two repos share no package).
 */
export const EPHEMERAL_CAMPAIGN_ID = '00000000-0000-4000-8000-deadbeefc0de';

export const isEphemeralCampaignId = (id?: string | null): boolean => id === EPHEMERAL_CAMPAIGN_ID;

/**
 * Sample CSV data for marketing/outreach campaigns
 */
export const csvSampleData = [
  ['email', 'user.display_name', 'phone'],
  ['john.doe@example.com', 'John Doe', '+306911000000'],
  ['jane.smith@example.com', 'Jane Smith', '+12345678901'],
  ['bob.johnson@example.com', 'Bob Johnson', '+12345678912'],
];

/**
 * Sample CSV data for debt collection/payment recovery campaigns
 * Includes all required debt fields
 */
export const debtCsvSampleData = [
  [
    'account_id',
    'user.display_name',
    'email',
    'phone',
    'debt.amount',
    'debt.currency',
    'debt.due_date',
    'debt.original_creditor',
    'debt.debt_external_id',
    'residence_state',
    'country_code',
    'alternate_phones',
    'alternate_emails',
  ],
  [
    'ACC-001',
    'John Doe',
    'john.doe@example.com',
    '+12025551234',
    '1250.00',
    'USD',
    '2024-02-15',
    'ABC Bank',
    '',
    'CA',
    'US',
    '+12025551235',
    'john.alt@example.com',
  ],
  [
    'ACC-002',
    'Jane Smith',
    'jane.smith@example.com',
    '+12025555678',
    '2500.50',
    'USD',
    '2024-02-29',
    'XYZ Credit',
    '',
    'TX',
    'US',
    '',
    '',
  ],
  [
    'ACC-003',
    'Bob Johnson',
    'bob.j@example.com',
    '+12025559012',
    '875.25',
    'USD',
    '2024-01-20',
    'First National',
    '',
    'NY',
    'US',
    '',
    '',
  ],
];

/**
 * Generate a short, human-readable random debt external ID.
 * `DEBT-` + 6 base36 chars; not cryptographic — just needs to avoid collisions
 * within one user's dev tenant so re-uploading the sample doesn't hit the
 * orchestrator's Flavor-2 REJECT for an already-linked debt.
 */
const generateDebtExternalId = (): string => {
  const suffix = Math.random().toString(36).slice(2, 8).toUpperCase().padEnd(6, '0');
  return `DEBT-${suffix}`;
};

/**
 * Build the payment-recovery sample CSV with fresh `debt_external_id` values
 * each call. Used by the "Download sample file" button so users can re-upload
 * without hitting `subscriber.debtExistsWithExtras` (PR #1839).
 */
export const getDebtCsvSampleData = (): string[][] =>
  debtCsvSampleData.map((row, idx) => {
    if (idx === 0) {
      return row;
    }
    return row.map((cell, colIdx) =>
      colIdx === debtCsvSampleData[0].indexOf('debt.debt_external_id')
        ? generateDebtExternalId()
        : cell
    );
  });

/**
 * Core variables for debt collection campaigns (all contexts).
 *
 * Note: `debt.debt_external_id` is the stable caller-owned ID for the debt
 * (formerly `external_account_number` — renamed platform-wide in 2026-04,
 * Orchestrator PR #1839 §3). Sepal/Casavetes refer to this as "Debt External
 * ID" in-product. When present, the orchestrator treats subscriber upload as
 * link-only for matching existing debts; extra fields trigger a 409 and the
 * user is pointed at the Debt API update route.
 *
 * Debt fields are marked `slotted: true` so the matcher can address them
 * across multiple debt blocks per subscriber: `debts[0].amount`,
 * `debts[1].amount`, ... up to 20.
 */
export const DEBT_CORE_VARIABLES: VariableDefinition[] = [
  { name: 'account_id', label: 'Account ID', required: false, icon: HashIcon },
  { name: 'user.display_name', label: 'Consumer full name', required: true, icon: UserRoundIcon },
  {
    name: 'debt.amount',
    label: 'Amount',
    required: true,
    slotted: true,
    groupKey: 'debt',
    icon: CircleDollarSignIcon,
  },
  {
    name: 'debt.currency',
    label: 'Currency',
    required: true,
    slotted: true,
    groupKey: 'debt',
    icon: TypeIcon,
  },
  {
    name: 'debt.due_date',
    label: 'Due date',
    required: false,
    slotted: true,
    groupKey: 'debt',
    icon: CalendarIcon,
  },
  {
    name: 'debt.original_creditor',
    label: 'Original creditor',
    required: false,
    slotted: true,
    groupKey: 'debt',
    icon: TypeIcon,
  },
  {
    name: 'debt.debt_external_id',
    label: 'Debt External ID',
    required: false,
    slotted: true,
    groupKey: 'debt',
    icon: HashIcon,
  },
  { name: 'country_code', label: 'Country code', required: true, icon: GlobeIcon },
  // Alternate contact arrays — orchestrator accepts up to 10, deduped server-side.
  // `multi: true` lets the user map several CSV columns to the same target;
  // `transformCsvWithMappings` collects the values into an array by encounter
  // order (slot-indexed under the hood, see setNestedValue).
  {
    name: 'alternate_phones',
    label: 'Alternate phone',
    required: false,
    icon: PhoneIcon,
    multi: true,
  },
  {
    name: 'alternate_emails',
    label: 'Alternate email',
    required: false,
    icon: MailIcon,
    multi: true,
  },
];

/**
 * Contact fields — at least one of (email, phone) required
 * (conditionalGroup: 'contact'). residence_state is NOT in this group:
 * orchestrator validates it independently (FDCPA — required when country
 * is US/BR) so callers surface it via `extraRequiredFields` when needed.
 */
export const DEBT_CONTACT_VARIABLES: VariableDefinition[] = [
  { name: 'email', label: 'Email address', conditionalGroup: 'contact', icon: MailIcon },
  { name: 'phone', label: 'Phone number', conditionalGroup: 'contact', icon: PhoneIcon },
  { name: 'residence_state', label: 'Residence state', icon: MapPinIcon },
];

/**
 * Get debt campaign variables
 */
export const getDebtCampaignVariables = (): VariableDefinition[] => [
  ...DEBT_CORE_VARIABLES,
  ...DEBT_CONTACT_VARIABLES,
];

/**
 * @deprecated Use getDebtCampaignVariables() instead
 */
export const DEBT_CAMPAIGN_VARIABLES = getDebtCampaignVariables();

/**
 * Display-only variables for AudienceSummarySidebar (before CSV upload)
 * Shows the required fields in the exact order from the Figma design
 * This is a presentation-layer list, not used for validation
 */
export const DEBT_SIDEBAR_DISPLAY_VARIABLES: VariableDefinition[] = [
  { name: 'user.display_name', label: 'Consumer full name', required: true, icon: UserRoundIcon },
  {
    name: 'contact_info',
    label: 'Mobile phone and/or email address',
    required: true,
    icon: BookUserIcon,
  },
  { name: 'debt.amount', label: 'Amount', required: true, icon: CircleDollarSignIcon },
  { name: 'debt.currency', label: 'Currency', required: true, icon: TypeIcon },
  { name: 'country_code', label: 'Country code', required: true, icon: GlobeIcon },
  { name: 'account_id', label: 'Account ID', required: false, icon: HashIcon },
  { name: 'debt.due_date', label: 'Due date', required: false, icon: CalendarIcon },
  { name: 'debt.original_creditor', label: 'Original creditor', required: false, icon: TypeIcon },
  { name: 'debt.debt_external_id', label: 'Debt External ID', required: false, icon: HashIcon },
  { name: 'residence_state', label: 'Residence state', required: false, icon: MapPinIcon },
  { name: 'alternate_phones', label: 'Alternate phone', required: false, icon: PhoneIcon },
  { name: 'alternate_emails', label: 'Alternate email', required: false, icon: MailIcon },
];

/**
 * Infobip error status codes mapping to translation keys
 * Based on Infobip API documentation for SMS and general errors
 * @see https://www.infobip.com/docs/essentials/response-status-and-error-codes
 */
/**
 * Compliance profile options for payment recovery campaigns
 */
export const COMPLIANCE_PROFILE_OPTIONS = [
  { value: 'default', labelKey: 'campaigns.setup.compliance_profile_default' },
  {
    value: 'us_debt_collection_first_party',
    labelKey: 'campaigns.setup.compliance_profile_us_first_party',
  },
  {
    value: 'us_debt_collection_third_party',
    labelKey: 'campaigns.setup.compliance_profile_us_third_party',
  },
  {
    value: 'br_debt_collection_third_party',
    labelKey: 'campaigns.setup.compliance_profile_br_third_party',
  },
  {
    value: 'gr_debt_collection_third_party',
    labelKey: 'campaigns.setup.compliance_profile_gr_third_party',
  },
] as const;

export type ComplianceProfileId = (typeof COMPLIANCE_PROFILE_OPTIONS)[number]['value'];

export const infobipErrorStatuses: Record<string, string> = {
  // User errors (Group 2)
  '323': 'campaigns.errors.invalid_request_type',
  '345': 'campaigns.errors.operator_not_found',
  '351': 'campaigns.errors.invalid_destination_address',
  '352': 'campaigns.errors.invalid_request_destination',
  '355': 'campaigns.errors.msg_text_too_long',
  '368': 'campaigns.errors.invalid_source_address',
  '433': 'campaigns.errors.account_access_denied',
  '541': 'campaigns.errors.limit_reached',
  '542': 'campaigns.errors.quota_reached',
  '572': 'campaigns.errors.campaignid_not_provisioned',
  '577': 'campaigns.errors.acc_not_provisioned_to_sms_demo_sc',
  '578': 'campaigns.errors.exceeded_the_time_limit_of_sms_demo',
  '579': 'campaigns.errors.dest_address_not_in_sms_demo',
  '580': 'campaigns.errors.exceeded_the_max_number',
  '594': 'campaigns.errors.invalid_global_campaign_id',
  '597': 'campaigns.errors.acc_has_no_address_for_dest',
  '598': 'campaigns.errors.interaction_not_supported',
  '601': 'campaigns.errors.acc_not_2_way',
  '603': 'campaigns.errors.content_blocked',
  '604': 'campaigns.errors.blocked_by_campaign_blacklist',
  '607': 'campaigns.errors.rejected_spam_by_operator',
  '608': 'campaigns.errors.signals_blocked',
  '609': 'campaigns.errors.signals_hard_limit_reached',
  '2049': 'campaigns.errors.imsi_blacklisted',
  '2052': 'campaigns.errors.blacklisted_destination_address',
  '2053': 'campaigns.errors.blacklisted_sender_address',
  '3041': 'campaigns.errors.deactivated_list',
  '4096': 'campaigns.errors.invalid_pdu_format',
  '4099': 'campaigns.errors.monthly_limit_reached',
  '4100': 'campaigns.errors.message_canceled',
  '4101': 'campaigns.errors.validity_expired',
  '4102': 'campaigns.errors.not_submitted_to_smpp_channel',
  '4103': 'campaigns.errors.destination_flooding',
  '4104': 'campaigns.errors.destination_txt_flooding',
  '4202': 'campaigns.errors.10dlc_limit_reached',
  // Operator errors (Group 3)
  '10': 'campaigns.errors.bearer_service_not_provisioned',
  '20': 'campaigns.errors.ss_incompatibility',
  '51': 'campaigns.errors.resource_limitation',
  '71': 'campaigns.errors.unknown_alphabet',
  '375': 'campaigns.errors.source_address_is_blocked',
  '488': 'campaigns.errors.tf_number_not_verified',
  '501': 'campaigns.errors.invalid_response_received',
  '560': 'campaigns.errors.sc_blocked_for_end_user',
  '561': 'campaigns.errors.content_blocked_by_operator',
  '562': 'campaigns.errors.sc_not_provisioned',
  '563': 'campaigns.errors.sc_expired',
  '564': 'campaigns.errors.sc_blocked',
  '566': 'campaigns.errors.destination_blocked_by_operator',
  '568': 'campaigns.errors.destination_not_sms_provisioned',
  '569': 'campaigns.errors.dest_address_suspended',
  '571': 'campaigns.errors.campaign_id_rejected',
  '574': 'campaigns.errors.blocked_new_subscriptions_for_sc',
  '592': 'campaigns.errors.account_not_provisioned_for_sms',
  '630': 'campaigns.errors.dest_overloaded',
  '631': 'campaigns.errors.mobile_operator_network_error',
  '632': 'campaigns.errors.sms_rejected_by_mobile_operator',
  '633': 'campaigns.errors.unknown_mobile_operator',
  '810': 'campaigns.errors.failed_message_delivery',
  '811': 'campaigns.errors.message_expired',
  '815': 'campaigns.errors.message_submitted_not_acked',
  '2048': 'campaigns.errors.time_out',
  '2050': 'campaigns.errors.dest_address_blacklisted',
  '2051': 'campaigns.errors.invalid_msc_address',
  '4097': 'campaigns.errors.not_submitted_to_gmsc',
  '5492': 'campaigns.errors.duplicate_message',
  // Handset errors (Group 1)
  '1': 'campaigns.errors.unknown_subscriber',
  '2': 'campaigns.errors.unknown_base_station',
  '5': 'campaigns.errors.unidentified_subscriber',
  '6': 'campaigns.errors.absent_subscriber_sm',
  '7': 'campaigns.errors.unknown_equipment',
  '8': 'campaigns.errors.roaming_not_allowed',
  '9': 'campaigns.errors.illegal_subscriber',
  '11': 'campaigns.errors.teleservice_not_provisioned',
  '12': 'campaigns.errors.illegal_equipment',
  '13': 'campaigns.errors.call_barred',
  '21': 'campaigns.errors.facility_not_supported',
  '27': 'campaigns.errors.absent_subscriber',
  '31': 'campaigns.errors.subscriber_busy_for_mt_sms',
  '32': 'campaigns.errors.sm_delivery_failure',
  '33': 'campaigns.errors.message_waiting_list_full',
  '34': 'campaigns.errors.system_failure',
  '35': 'campaigns.errors.data_missing',
  '36': 'campaigns.errors.unexpected_data_value',
  '255': 'campaigns.errors.unknown_error',
  '256': 'campaigns.errors.sm_df_memory_capacity_exceeded',
  '257': 'campaigns.errors.sm_df_equipment_protocol_error',
  '258': 'campaigns.errors.sm_df_equipment_not_sm_equipped',
  '259': 'campaigns.errors.sm_df_unknown_service_centre',
  '260': 'campaigns.errors.sm_df_sc_congestion',
  '261': 'campaigns.errors.sm_df_invalid_sme_address',
  '262': 'campaigns.errors.sm_df_subscriber_not_sc_subscriber',
};

// Telnyx Voice API error codes mapped to i18n message keys. Codes are raw
// Telnyx error codes (https://developers.telnyx.com/development/api-fundamentals/api-errors)
// surfaced verbatim from channels via the campaign subscriber receipt.
export const telnyxErrorStatuses: Record<string, string> = {
  // 10010 is "Authorization failed" per Telnyx docs. Telnyx also reuses it for
  // some routing-layer rejections (PE5 surfaces here with detail "No Routes Found").
  // The accompanying SIP code lives in `telnyx_error.error_code` which we don't
  // currently surface; if we do later we can map PE5 specifically.
  '10010': 'campaigns.errors.telnyx_authorization_failed',
  '90015': 'campaigns.errors.telnyx_invalid_call_control_id',
  '90018': 'campaigns.errors.telnyx_call_already_ended',
  '90034': 'campaigns.errors.telnyx_invalid_call_state',
};
